import { DateRangePeriod, ApiSummaryStatus, ApiRunConfigStatus } from './enumerations';

export const API_ERROR_MESSAGES = {
  UNKNOWN_ERROR: 'Unknown error. Please try again later.',
  SERVICE_UNAVAILABLE: 'Service is currently unavailable. Please try again later.',
  TOO_MANY_REQUESTS:
    'It looks like our system is receiving too many requests at the moment. Please wait a few moments and try again.',
  SESSION_CREATION_ERROR: 'Failed to create a session. Please refresh the page and try again.',
  MAX_TOKENS_EXCEEDED: 'File size exceeds system processing. Please upload a smaller file.',
  UPLOAD_ERROR: 'Upload Failed',
  SUMMARY_STATUS_ERROR:
    'Something went wrong! We encountered an unexpected issue while processing your request.',
  REQUEST_CANCELED: 'Request canceled',
  NETWORK_ERROR: 'A network error occurred while processing your request.',
  NOT_FOUND_RUN: 'No summary found for this run. Please check the run ID and try again.',
  FAIL_TO_FETCH_RUN: 'Something went wrong while loading the summary. Please try again later.',
};

export const MAX_PART_SIZE = 100 * 1024 * 1024; // 100MB

export const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB
export const SUPPORT_DOWNLOAD_FORMATS = ['.csv', '.xls', '.xlsx', '.pdf'];

export const SALES_OUTCOME_TABLE_FIELDS = [
  { label: 'Loan Advisor Name', key: 'sales_outcome_loan_advisor_name' },
  { label: 'Current Rate (%)', key: 'sales_outcome_current_rate_percent' },
  { label: 'Offered Rate (%)', key: 'sales_outcome_offered_rate_percent' },
  { label: 'Savings ($)', key: 'sales_outcome_savings_dollars' },
  { label: 'Offer Accepted? (Y/N)', key: 'sales_outcome_offer_accepted_yn' },
  { label: 'Reason for accepting if mentioned?', key: 'sales_outcome_reason_for_accepting' },
  { label: 'Accept Category', key: 'sales_outcome_accept_category' },
  { label: 'Offer Declined? (Y/N)', key: 'sales_outcome_offer_declined_yn' },
  { label: 'Decline Reason', key: 'sales_outcome_decline_reason' },
  { label: 'Decline Category', key: 'sales_outcome_decline_category' },
  { label: 'Borrower objections? (Y/N)', key: 'sales_outcome_borrower_objections_yn' },
  { label: 'Objection Category', key: 'sales_outcome_objection_category' },
  { label: 'Objections overcome? (Y/N)', key: 'sales_outcome_objections_overcome_yn' },
  { label: 'Objections overcome Category', key: 'sales_outcome_objections_overcome_category' },
  {
    label: 'Competitive offer mentioned? (Y/N)',
    key: 'sales_outcome_competitive_offer_mentioned_yn',
  },
  { label: 'Company with the competitive offer', key: 'sales_outcome_competitive_offer_company' },
  { label: 'Competitive offer (% and points)', key: 'sales_outcome_competitive_offer_details' },
  { label: 'Pitch made? (Y/N)', key: 'sales_outcome_pitch_made_yn' },
  { label: 'Other commentary about the script', key: 'sales_outcome_script_commentary' },
];

export const SCRIPT_ADHERENCE_CHECKPOINTS = [
  { key: 'script_adherence_introduction', label: 'Introduction' },
  { key: 'script_adherence_reason_for_call', label: 'Reason for Call' },
  { key: 'script_adherence_verification', label: 'Verification' },
  { key: 'script_adherence_offer_presentation', label: 'Offer Presentation' },
  { key: 'script_adherence_handling_objections', label: 'Handling Objections' },
  { key: 'script_adherence_verifying_1003_information', label: 'Verifying 1003 Information' },
  { key: 'script_adherence_declaration_questions', label: 'Declaration Questions' },
  { key: 'script_adherence_government_monitoring', label: 'Government Monitoring' },
  { key: 'script_adherence_closing_timeline', label: 'Closing Timeline' },
  { key: 'script_adherence_wrap_up_and_next_steps', label: 'Wrap-Up and Next Steps' },
  {
    key: 'script_adherence_compliance_state_specific_requirements',
    label: 'Compliance: State-Specific Requirements',
  },
] as const;

export const ONLINE_ASSISTANCE_FIELDS = [
  {
    key: 'online_account_assistance_borrower_had_account',
    label: 'Did the borrower already have an online account with the company?',
  },
  {
    key: 'online_account_assistance_walkthrough',
    label: 'Did the loan advisor walk the borrower through the online account creation process?',
  },
  {
    key: 'online_account_assistance_customer_signed_documents',
    label: 'Did the customer sign the documents electronically during the call?',
  },
  {
    key: 'online_account_assistance_mention_streamline',
    label: 'Did the loan advisor mention streamline (Y/N)?',
  },
] as const;

export const FAILED_SUMMARY_STATUSES = [
  ApiSummaryStatus.SummarizationFailure,
  ApiSummaryStatus.FlattenFailure,
] as string[];

export const FINAL_SUMMARY_STATUSES = [
  ApiSummaryStatus.SummarizationSavedOnline,
  ...FAILED_SUMMARY_STATUSES,
] as string[];

export const FINAL_RUN_CONFIG_STATUSES = [
  ApiRunConfigStatus.Completed,
  ApiRunConfigStatus.Failed,
] as string[];

export const DIALOG_SUCCESS_UPLOAD_MESSAGE =
  'Your file has been successfully uploaded. The summarization process may take a few more minutes. Please check the status in the table.';

export const VALID_FILE_TYPES = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'video/mp4'];

export const MINIMUM_VISIBLE_COUNT = 10;

export const MAX_WIDTHS = {
  TRANSCRIPTION_CONTENT: 792,
  SALES_TABLE_CELL_FILE_NAME: 570,
  COMPLIANCE_TABLE_CELL_FILE_NAME: 250,
  FOOTER: 202,
};

export const MAX_HEIGHTS = {
  SECTION_CONTENT: 790,
  SUBHEADER: 70,
  SEARCH_MODAL_CONTENT: 400,
  METRIC_CARD: 180,
  FOOTER: 72,
  GOAL_TRACKER_CARD: 41,
  CHAT_CONTAINER_MARGIN: 150,
};

// export const MIN_WIDTHS = {
//   DOWNLOAD_SUMMARY_BUTTON: 190,
// };

export const MAX_PDF_SIZE = 100 * 1024 * 1024; // 100MB

export const ACCEPTED_AUDIO_FILE_TYPES = {
  'audio/*': ['.mp3', '.wav'],
  'video/*': ['.mp4'],
};

export const EMPTY_CONTENT_MESSAGES = {
  GENERAL: 'No data to display',
  CALL_TRANSCRIPTION: 'No Call Transcription data to display',
  SUMMARYTABLE: 'No Summary data to display',
  DEMOGRAPHICS: 'No Demographics data to display',
  SCRIPT_ADHERENCE: 'No Script Adherence data to display',
  FORM_1003: 'No Form 1003 data to display',
  TABLE_WITH_FILTERS: 'No data available for the selected filters.',
  SEARCH_MODAL:
    'Enter a loan #, sales manager, loan advisor, or phone number to find relevant records',
};

export const ERROR_MESSAGES = {
  NO_PDF_UPLOADED: 'Audio file cannot be compared as there is no PDF uploaded',
};

export const MSAL_SCOPES = ['profile openid email User.Read'];

export const INTENT_SEARCH_VISIBLE_COUNT = 10;

export const ONLINEASSISTANCE_CHECKPOINTS = [
  {
    key: 'borrower_had_account',
    label: 'Did the borrower already have an online account with the company?',
  },
  {
    key: 'walkthrough',
    label: 'Did the loan advisor walk the borrower through the online account creation process?',
  },
  {
    key: 'customer_signed_documents',
    label: 'Did the customer sign the documents electronically during the call?',
  },
  {
    key: 'mention_streamline',
    label: 'Did the loan advisor mention streamline (Y/N)?',
  },
];

// Session storage keys
export const SESSION_STORAGE_KEYS = {
  SESSION_ID: 'x-session-id',
  CORRELATION_ID: 'correlationId',
  USER_ROLE_ID: 'call-summ:user-role-id',
  USER_ROLE_SYNC_TIMESTAMP: 'call-summ:user-role-sync-timestamp',
  SEARCH: 'call-summ:search',
  PREVIOUS_LOCATION: 'call-summ:previous-location',
  WELCOME_MESSAGE_SHOWN: 'call-summ:welcome-message-shown',
};

// Local storage keys
export const LOCAL_STORAGE_KEYS = {
  USER_ID: 'call-summ:user-id',
  AUTH_TOKEN: 'call-summ:auth-token',
  SM_VIEW_ROWS_PER_PAGE: 'call-summ:sm-view-rows-per-page',
  LA_VIEW_ROWS_PER_PAGE: 'call-summ:la-view-rows-per-page',
  HALO_DISCLAIMER_HIDDEN: 'call-summ:halo-disclaimer-hidden',
};

export const CACHE_DURATION = {
  ANALYTICS_REFRESH: 60 * 60 * 1000, // 1 hour
  ANALYTICS_CALLS: 5 * 60 * 1000, // 5 minutes
  SALES_SEARCH: 60 * 60 * 1000, // 1 hour
};

export const VALID_DATE_RANGE_PERIODS = [DateRangePeriod.Yesterday, DateRangePeriod.ThisWeek];

export const POLLING_INTERVALS = {
  DEFAULT: 3000, // 3 seconds
  SHORT: 1500, // 1.5 seconds
  LONG: 6000, // 6 seconds
};

export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

export const HALO_CHAT_TEXT = {
  INTRO: "Hi! I'm Halo, your AI Assistant. How can I help?",
  ERROR: "Sorry, I couldn't connect. Give me a moment to set things right.",
};

export const ANALYTICS_ALLOWED_DATES = [
  // July 6 to July 12, 2025
  new Date(2025, 6, 6),
  new Date(2025, 6, 7),
  new Date(2025, 6, 8),
  new Date(2025, 6, 9),
  new Date(2025, 6, 10),
  new Date(2025, 6, 11),
  new Date(2025, 6, 12),
  // August 4 to August 8, 2025
  new Date(2025, 7, 4),
  new Date(2025, 7, 5),
  new Date(2025, 7, 6),
  new Date(2025, 7, 7),
  new Date(2025, 7, 8),
];

export const CHATBOT_PROMPTS = {
  LOAN_ADVISOR_DASHBOARD:
    'Displays loan advisor performance analytics for a selected time period (defined by a start date and an end date). The results show the advisor’s overall performance within that timeframe, along with a list of calls made. _change values: difference compared to the same previous period. _total values: expected targets for the period. _current values: actual results achieved within the period.',
  SALES_MANAGER_DASHBOARD:
    'Displays sales manager performance analytics for a selected time period (defined by a start date and an end date). The results show the manager’s overall team performance, along with a list of supervised loan advisors and their individual results. _change values: difference compared to the same previous period. _total values: expected targets for the period. _current values: actual results achieved within the period.',
};

// Possibly intended for use across multiple pipelines.
export const RUN_CONFIG_TIMEOUT_MINUTES = 15;
